// /app/content/new — composer.
//
// Layout:
//   lg+:  2-column split. Left = composer form, right = sticky live preview.
//   <lg:  single column; preview moves below the form.
//
// Top: platform tab strip with icons. Multi-select; when >1 selected,
// a small tab control above the preview lets the operator cycle
// through which platform is being previewed.
//
// Behaviour preserved from the previous design:
//   - prefilled brand via ?brand_id=
//   - reference-asset gallery sourced from brand.assets type=example
//   - up to 5 refs, server cap enforced server-side
//   - on submit → navigate to /app/generations/{id}

import { useEffect, useMemo, useState, type ReactElement } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Button } from "../../../components/Button";
import { Select } from "../../../components/Select";
import { PostPreview, platformCharLimit, charCountLevel } from "../../../components/PostPreview";
import { GenerateProgressOverlay } from "../../../components/GenerateProgress";
import { useI18n } from "../../../lib/i18n";
import {
  ApiError,
  assetURL,
  brands as brandsAPI,
  generations,
  type Brand,
  type BrandAsset,
  type ContentType,
  type Platform,
} from "../../../lib/api";
import {
  CONTENT_TYPES as CONTENT_TYPES_TAXA,
  PLATFORMS as PLATFORMS_TAXA,
  labelOf,
} from "../../../lib/brand-taxonomies";

// Inline SVG icons per platform — preferred over emoji for the tab
// strip because they keep their color/weight against the dark surface.
const PLATFORM_ICONS: Record<Platform, ReactElement> = {
  facebook: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.77-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.77l-.44 2.9h-2.33v6.99A10 10 0 0 0 22 12Z"/>
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5"/>
      <circle cx="12" cy="12" r="3.5"/>
      <circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
      <path d="M4.98 3.5a2.5 2.5 0 1 1-.01 5.01A2.5 2.5 0 0 1 4.98 3.5ZM3 9h4v12H3V9Zm6 0h3.84v1.71h.06c.53-.95 1.84-1.95 3.79-1.95C20.6 8.76 22 11 22 14.16V21h-4v-6.13c0-1.46-.03-3.34-2.04-3.34-2.04 0-2.36 1.59-2.36 3.23V21h-4V9Z"/>
    </svg>
  ),
  telegram: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
      <path d="M9.04 15.6 8.86 19c.41 0 .59-.18.8-.4l1.92-1.82 3.99 2.91c.73.4 1.25.19 1.45-.67l2.62-12.27c.24-1.09-.39-1.51-1.1-1.25L3.21 9.95c-1.06.41-1.04 1.01-.18 1.28l4.18 1.31 9.7-6.11c.46-.3.88-.13.53.18L9.04 15.6Z"/>
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden>
      <path d="M18.244 2H21.5l-7.55 8.626L23 22h-6.81l-5.346-6.99L4.7 22H1.44l8.077-9.23L1 2h6.98l4.835 6.39L18.244 2Zm-1.196 18h1.886L7.04 4H5.02l12.028 16Z"/>
    </svg>
  ),
};

function NewGeneration() {
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const search = Route.useSearch();
  const prefilledBrandId = (search as { brand_id?: string }).brand_id ?? "";

  const [brandsList, setBrandsList] = useState<Brand[] | null>(null);
  const [brandId, setBrandId] = useState<string>(prefilledBrandId);
  const [prompt, setPrompt] = useState("");
  // v0.2.48 — Post type drives canvas + platform allow-list. Story
  // mode disables LinkedIn/X/Telegram chips and forces image-only.
  const [postType, setPostType] = useState<"feed" | "story">("feed");
  const [platforms, setPlatforms] = useState<Platform[]>(["instagram"]);
  const [types, setTypes] = useState<ContentType[]>(["text"]);
  const [variantsPerPlatform, setVariantsPerPlatform] = useState(1);
  const [examples, setExamples] = useState<BrandAsset[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errs, setErrs] = useState<{ form?: string; prompt?: string; platforms?: string; brand?: string }>({});

  // Story mode: only IG and FB have story APIs. When story is
  // selected, automatically drop any non-story platforms from the
  // selection — better UX than letting the operator submit a
  // backend-400 mistake.
  const STORY_PLATFORMS: Platform[] = ["instagram", "facebook"];
  useEffect(() => {
    if (postType === "story") {
      setPlatforms((cur) => {
        const filtered = cur.filter((p) => STORY_PLATFORMS.includes(p));
        return filtered.length > 0 ? filtered : ["instagram"];
      });
      // Stories are image-only; backend forces this server-side but
      // the UI should reflect the constraint too.
      setTypes(["image"]);
    }
  }, [postType]);

  // Platform-aware content_types enforcement.
  // Instagram (and TikTok in the future) is image-mandatory — the
  // upstream platforms reject text-only posts. The other platforms
  // (FB, LinkedIn, Telegram) accept either text-only OR text+image.
  // When the user has IG in their selection, force "image" on and
  // lock that toggle. When they remove IG, the user can choose again.
  const requiresImage = platforms.some((p) => p === "instagram") || postType === "story";
  useEffect(() => {
    if (requiresImage && !types.includes("image")) {
      setTypes((cur) => [...cur, "image"]);
    }
  }, [requiresImage, types]);

  // Preview-platform — which of the selected platforms is rendered
  // in the right pane. Auto-snaps to the first selected when the
  // current preview platform is removed from the selection.
  const [previewPlatform, setPreviewPlatform] = useState<Platform>("instagram");
  useEffect(() => {
    if (platforms.length === 0) return;
    if (!platforms.includes(previewPlatform)) {
      setPreviewPlatform(platforms[0]!);
    }
  }, [platforms, previewPlatform]);

  useEffect(() => {
    let cancelled = false;
    brandsAPI.list()
      .then((r) => {
        if (cancelled) return;
        setBrandsList(r.brands);
        if (!brandId && r.brands.length > 0) {
          setBrandId(r.brands[0]!.id);
        }
      })
      .catch(() => !cancelled && setBrandsList([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentBrand = useMemo(
    () => (brandsList ?? []).find((b) => b.id === brandId) ?? null,
    [brandsList, brandId],
  );

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    setSelectedRefs([]);
    // v0.5.22 — pull every kind the operator may want to reference,
    // not just kind=example. Earlier flow only fetched examples and
    // hid the brand's logos / products / team photos from the
    // reference picker. After the auto-promote (v0.5.22) on TradeGPT
    // 14 of 15 logos moved out of kind=example so the picker was
    // showing 1 image. Now we union the three referenceable kinds.
    //
    // The brand-service listAssets endpoint accepts ONE kind at a
    // time; fan out + merge client-side. Kept under 3 calls (no
    // perf concern at brand-asset scale ≤ ~100/brand).
    const KINDS_TO_FETCH = ["example", "logo", "product"] as const;
    Promise.all(
      KINDS_TO_FETCH.map((k) =>
        brandsAPI.listAssets(brandId, k).catch(() => ({ assets: [] as BrandAsset[] })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const merged: BrandAsset[] = [];
      const seen = new Set<string>();
      for (const r of results) {
        for (const a of r.assets) {
          if (!seen.has(a.id)) {
            seen.add(a.id);
            merged.push(a);
          }
        }
      }
      // Sort: most-recent first. uploaded_at is ISO so string sort works.
      merged.sort((a, b) => (b.uploaded_at ?? "").localeCompare(a.uploaded_at ?? ""));
      setExamples(merged);
    });
    return () => { cancelled = true; };
  }, [brandId]);

  // v0.5.28 — connected-platform gating. Without this, operators could
  // pick a platform that the brand hasn't connected, generate content,
  // then fail at publish-time with no clear path forward. Operator
  // flagged this 2026-05-22.
  //
  // For OAuth platforms (FB / IG / LinkedIn / X), the platform is
  // "connected" when there's an active row in brand.social_accounts.
  // Telegram is different — it uses brand.telegram_channel (set in
  // the Brand Studio integrations panel), no OAuth needed.
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<Platform>>(new Set());
  useEffect(() => {
    if (!brandId) {
      setConnectedPlatforms(new Set());
      return;
    }
    let cancelled = false;
    Promise.all([
      brandsAPI.listSocialAccounts(brandId).catch(() => ({ social_accounts: [] })),
      brandsAPI.get(brandId).catch(() => null),
    ]).then(([sa, br]) => {
      if (cancelled) return;
      const set = new Set<Platform>();
      for (const a of sa.social_accounts ?? []) {
        if (a.status === "active") set.add(a.platform as Platform);
      }
      // Telegram has its own connect mechanism (channel handle), not OAuth.
      if (br?.telegram_channel && br.telegram_channel.trim() !== "") {
        set.add("telegram" as Platform);
      }
      setConnectedPlatforms(set);
    });
    return () => { cancelled = true; };
  }, [brandId]);

  // Auto-drop platforms the operator had selected that are now NOT
  // connected (e.g. they switched brands and the previous brand had
  // FB but the new one doesn't). Prevents stale selection submitting.
  useEffect(() => {
    if (connectedPlatforms.size === 0) return;
    setPlatforms((cur) => cur.filter((p) => connectedPlatforms.has(p)));
  }, [connectedPlatforms]);

  function toggleRef(id: string) {
    setSelectedRefs((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 5) return cur;
      return [...cur, id];
    });
  }
  function togglePlatform(p: Platform) {
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
  }
  function toggleType(c: ContentType) {
    // Block toggling off "image" while Instagram is in the platform list —
    // IG rejects text-only posts, so the toggle would just produce a publish
    // failure later. Force user to either remove IG or keep image checked.
    if (c === "image" && requiresImage && types.includes("image")) return;
    setTypes((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: typeof errs = {};
    if (!brandId) next.brand = t("gen.field.brand");
    if (!prompt.trim()) next.prompt = t("gen.field.prompt");
    if (platforms.length === 0) next.platforms = t("gen.field.platforms.required");
    setErrs(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const res = await generations.create({
        brand_id: brandId,
        prompt: prompt.trim(),
        target_platforms: platforms,
        content_types: types.length > 0 ? types : ["text"],
        variants_per_platform: variantsPerPlatform,
        reference_asset_ids: selectedRefs.length > 0 ? selectedRefs : undefined,
        post_type: postType, // v0.2.48
      });
      void nav({ to: "/app/generations/$generationId", params: { generationId: res.generation.id }, replace: true });
    } catch (e2) {
      if (e2 instanceof ApiError) {
        setErrs({ form: e2.message });
      } else {
        setErrs({ form: t("common.errorGeneric") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (brandsList == null) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="rounded-[14px] bg-surface border border-border-subtle p-6">
          <p className="text-secondary text-sm">{t("common.loading")}</p>
        </div>
      </div>
    );
  }
  if (brandsList.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <div className="rounded-[14px] bg-surface border border-border-subtle p-8 flex flex-col items-center text-center gap-4">
          <span className="eyebrow eyebrow-pill inline-flex">{t("content.pickBrand")}</span>
          <p className="text-secondary text-sm">{t("brands.empty.subtitle")}</p>
          <Link to="/app/brands/new">
            <Button variant="primary">+ {t("brands.new")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const charsUsed = prompt.length;
  const limit = previewPlatform ? platformCharLimit(previewPlatform) : -1;
  const level = charCountLevel(charsUsed, limit);
  const firstRefImage = selectedRefs
    .map((id) => examples.find((e) => e.id === id))
    .find((a) => a?.mime_type.startsWith("image/"));
  const previewImageURL = firstRefImage ? assetURL(firstRefImage.url) : undefined;

  return (
    <div className="min-h-full">
      {/* Sticky top bar — title + close */}
      <div className="sticky top-14 z-10 bg-shell/85 backdrop-blur border-b border-border-subtle">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex items-center gap-3">
          <span className="eyebrow hidden sm:inline">{t("content.eyebrow")}</span>
          <h1 className="font-display text-lg sm:text-xl font-semibold flex-1 truncate">
            {t("gen.create.title")}
          </h1>
          <Link to="/app/content" className="text-sm text-secondary hover:text-primary ring-nexus rounded">
            {t("brands.detail.cancel")}
          </Link>
        </div>
      </div>

      {/* Full-screen progress overlay while POST /v1/generations is in flight.
          Covers the 30-75s sync wait with a time-anchored phase animation
          + escape-hatch message past 90s pointing at the gallery — Linkerd
          sometimes cuts the response stream before it lands in the browser
          (task #9) so this is the user's "your work is safe, look here" cue. */}
      <GenerateProgressOverlay
        open={submitting}
        platformCount={platforms.length}
        variantsPerPlatform={variantsPerPlatform}
      />

      <form onSubmit={onSubmit} noValidate
        className="mx-auto max-w-7xl px-4 sm:px-6 py-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)]">
        {/* ============== LEFT: composer form ============== */}
        <div className="flex flex-col gap-5">
          {/* Brand picker */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5">
            <Select
              label={t("gen.field.brand")}
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              options={brandsList.map((b) => ({ value: b.id, label: b.trade_name }))}
              errorText={errs.brand ?? null}
            />
          </div>

          {/* Post type — v0.2.48. Feed vs Story. Story locks platforms
              to IG + FB, forces image-only, drives 9:16 canvas. */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              {t("gen.field.postType")}
            </span>
            <div className="flex flex-wrap gap-2">
              {(["feed", "story"] as const).map((pt) => {
                const on = postType === pt;
                return (
                  <button
                    key={pt}
                    type="button"
                    onClick={() => setPostType(pt)}
                    aria-pressed={on}
                    className={[
                      "ring-nexus rounded-[10px] px-4 py-2 text-sm border transition flex items-center gap-2",
                      on
                        ? "bg-red text-white border-transparent shadow-md shadow-red/20"
                        : "bg-elevated border-border-default text-secondary hover:text-primary hover:border-border-strong",
                    ].join(" ")}
                  >
                    <span>{pt === "feed" ? "📰" : "📱"}</span>
                    <span>{t(`gen.field.postType.${pt}`)}</span>
                  </button>
                );
              })}
            </div>
            {postType === "story" ? (
              <p className="text-xs text-muted">
                {t("gen.field.postType.storyHint")}
              </p>
            ) : null}
          </div>

          {/* Platform tab strip — large icons, clearly clickable */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              {t("gen.field.platforms")}
            </span>
            <div className="flex flex-wrap gap-2">
              {PLATFORMS_TAXA.map((p) => {
                const v = p.value as Platform;
                const on = platforms.includes(v);
                // v0.2.48 — story mode: disable LinkedIn/X/Telegram chips.
                const storyDisabled = postType === "story" && !STORY_PLATFORMS.includes(v);
                // v0.5.28 — disable chips for platforms the brand
                // hasn't connected. Telegram is connected via
                // brand.telegram_channel (no OAuth), others via
                // social_accounts. Without this, the operator could
                // generate content and then fail at publish time.
                const notConnected = brandId !== "" && !connectedPlatforms.has(v);
                const disabled = storyDisabled || notConnected;
                const disabledReason = storyDisabled
                  ? t("gen.field.platforms.noStoryApi")
                  : notConnected
                    ? t("gen.field.platforms.notConnected")
                    : undefined;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => !disabled && togglePlatform(v)}
                    aria-pressed={on}
                    disabled={disabled}
                    title={disabledReason}
                    className={[
                      "ring-nexus rounded-[10px] px-3 py-2 text-sm border transition flex items-center gap-2",
                      disabled
                        ? "bg-elevated border-border-subtle text-muted opacity-40 cursor-not-allowed"
                        : on
                          ? "bg-red text-white border-transparent shadow-md shadow-red/20"
                          : "bg-elevated border-border-default text-secondary hover:text-primary hover:border-border-strong",
                    ].join(" ")}
                  >
                    <span className={on ? "text-white" : "text-secondary"}>
                      {PLATFORM_ICONS[v] ?? null}
                    </span>
                    <span>{labelOf(p, locale)}</span>
                    {notConnected && !storyDisabled ? (
                      <span aria-hidden className="text-[10px] opacity-70">🔒</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {/* Hint when fewer than all platforms are connected — give
                the operator a one-click jump to the integrations page. */}
            {brandId && connectedPlatforms.size < PLATFORMS_TAXA.length ? (
              <p className="text-xs text-muted">
                {t("gen.field.platforms.connectHint")}{" "}
                <Link
                  to="/app/brands/$brandId"
                  params={{ brandId }}
                  hash="integrations"
                  className="text-info hover:text-primary ring-nexus rounded underline"
                >
                  {t("gen.field.platforms.connectCTA")}
                </Link>
              </p>
            ) : null}
            {errs.platforms ? <span className="text-xs text-error">{errs.platforms}</span> : null}
          </div>

          {/* Prompt — the headline interaction */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
                {t("gen.field.prompt")}
              </span>
              {limit > 0 ? (
                <span className={[
                  "text-[11px] font-mono",
                  level === "over" ? "text-error" : level === "near" ? "text-warning" : "text-muted",
                ].join(" ")}>
                  {charsUsed} / {limit}
                </span>
              ) : null}
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={6}
              placeholder={t("gen.field.promptHint")}
              autoFocus={!!brandId && !prefilledBrandId}
              className={[
                "ring-nexus rounded-[10px] bg-shell border text-primary px-3.5 py-2.5 text-sm resize-y min-h-[140px]",
                errs.prompt ? "border-error" : "border-border-default",
              ].join(" ")}
            />
            {errs.prompt ? <span className="text-xs text-error">{errs.prompt}</span> : null}
          </div>

          {/* Content types */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              {t("gen.field.contentTypes")}
            </span>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES_TAXA.map((c) => {
                const v = c.value as ContentType;
                const on = types.includes(v);
                const locked = v === "image" && requiresImage && on;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleType(v)}
                    aria-pressed={on}
                    aria-disabled={locked}
                    title={locked ? "Required because Instagram is selected" : undefined}
                    className={[
                      "ring-nexus rounded-[10px] px-4 py-2 text-sm border transition flex items-center gap-1.5",
                      on
                        ? "bg-red text-white border-transparent"
                        : "bg-elevated border-border-default text-secondary hover:text-primary",
                      locked ? "cursor-not-allowed opacity-90" : "",
                    ].join(" ")}
                  >
                    {labelOf(c, locale)}
                    {locked ? <span aria-hidden className="text-[10px] opacity-70">🔒</span> : null}
                  </button>
                );
              })}
            </div>
            {requiresImage ? (
              <p className="text-[11px] text-muted">
                Image is required because Instagram is selected — IG doesn't accept text-only posts.
              </p>
            ) : null}
          </div>

          {/* Reference examples gallery */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
                {t("gen.field.references")}
              </span>
              {examples.length > 0 ? (
                <span className="text-[11px] font-mono text-muted">{selectedRefs.length}/5</span>
              ) : null}
            </div>
            {examples.length === 0 ? (
              <p className="text-xs text-muted">
                {t("gen.field.referencesEmpty")}{" "}
                {brandId ? (
                  <Link
                    to="/app/brands/$brandId"
                    params={{ brandId }}
                    hash="examples"
                    className="text-info hover:text-primary ring-nexus rounded underline"
                  >
                    {t("gen.field.referencesEmptyCTA")}
                  </Link>
                ) : null}
              </p>
            ) : (
              <>
                <p className="text-xs text-muted">{t("gen.field.referencesHint")}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {examples.map((a) => {
                    const on = selectedRefs.includes(a.id);
                    const disabled = !on && selectedRefs.length >= 5;
                    const isImg = a.mime_type.startsWith("image/");
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => toggleRef(a.id)}
                        disabled={disabled}
                        aria-pressed={on}
                        className={[
                          "ring-nexus rounded-[10px] overflow-hidden border-2 aspect-square relative transition",
                          on ? "border-red shadow-md shadow-red/20" : "border-border-default hover:border-border-strong",
                          disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer",
                        ].join(" ")}
                        title={a.filename_original}
                      >
                        {isImg ? (
                          <img
                            src={assetURL(a.url)}
                            alt={a.filename_original}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono text-muted">
                            {a.mime_type.split("/")[1]?.toUpperCase()}
                          </span>
                        )}
                        {on ? (
                          <span className="absolute top-1 right-1 bg-red text-white rounded-full w-5 h-5 flex items-center justify-center text-[11px] font-bold">
                            ✓
                          </span>
                        ) : null}
                        {/* v0.5.22 — kind badge so the operator can tell
                            logos / products / examples apart at a glance
                            now that the picker shows multiple kinds. */}
                        <span
                          className={[
                            "absolute bottom-1 left-1 rounded px-1 py-0.5 text-[9px] font-mono uppercase tracking-wider",
                            a.kind === "logo"
                              ? "bg-info/80 text-white"
                              : a.kind === "product"
                                ? "bg-success/80 text-white"
                                : "bg-elevated/80 text-secondary",
                          ].join(" ")}
                        >
                          {a.kind}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Variants */}
          <div className="rounded-[14px] bg-surface border border-border-subtle p-4 sm:p-5 flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
                {t("gen.field.variants")}
              </span>
              <span className="text-[11px] text-muted">{t("gen.field.variantsHint")}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4].map((n) => {
                const on = variantsPerPlatform === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setVariantsPerPlatform(n)}
                    aria-pressed={on}
                    className={[
                      "ring-nexus rounded-[10px] px-4 py-2 text-sm border transition min-w-[3rem]",
                      on
                        ? "bg-red text-white border-transparent"
                        : "bg-elevated border-border-default text-secondary hover:text-primary",
                    ].join(" ")}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>

          {errs.form ? (
            <div className="rounded-[10px] border border-error/40 bg-error/10 px-3 py-2 text-error text-sm" role="alert">
              {errs.form}
            </div>
          ) : null}

          {/* Action bar — sticks to the bottom of the viewport on
              small screens, sits inline on lg+ */}
          <div className="sticky bottom-0 -mx-4 sm:-mx-5 px-4 sm:px-5 py-3 bg-shell/90 backdrop-blur border-t border-border-subtle flex items-center justify-end gap-3 lg:static lg:mx-0 lg:bg-transparent lg:border-0 lg:p-0 lg:pt-2">
            <Link to="/app/content" className="text-sm text-secondary hover:text-primary ring-nexus rounded">
              {t("brands.detail.cancel")}
            </Link>
            <Button type="submit" loading={submitting}>
              {submitting ? t("gen.create.generating") : `✨ ${t("gen.create.submit")}`}
            </Button>
          </div>
        </div>

        {/* ============== RIGHT: live preview ============== */}
        <aside className="lg:sticky lg:top-[6.5rem] lg:self-start flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              {locale === "tr" ? "Önizleme" : "Preview"}
            </span>
            {platforms.length > 1 ? (
              <span className="text-[11px] text-muted">
                {locale === "tr"
                  ? `${platforms.indexOf(previewPlatform) + 1} / ${platforms.length}`
                  : `${platforms.indexOf(previewPlatform) + 1} of ${platforms.length}`}
              </span>
            ) : null}
          </div>

          {/* Platform tabs above the preview (only when >1 selected) */}
          {platforms.length > 1 ? (
            <div className="flex gap-1 rounded-[10px] bg-elevated p-1">
              {platforms.map((p) => {
                const taxon = PLATFORMS_TAXA.find((x) => x.value === p);
                const on = previewPlatform === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPreviewPlatform(p)}
                    aria-pressed={on}
                    className={[
                      "ring-nexus rounded-[8px] flex-1 py-1.5 text-xs flex items-center justify-center gap-1.5 transition",
                      on ? "bg-shell text-primary shadow" : "text-secondary hover:text-primary",
                    ].join(" ")}
                  >
                    <span>{PLATFORM_ICONS[p]}</span>
                    <span className="hidden sm:inline">{taxon ? labelOf(taxon, locale) : p}</span>
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="rounded-[14px] bg-elevated border border-border-subtle p-4 sm:p-5 flex items-start justify-center min-h-[420px]">
            {platforms.length === 0 ? (
              <p className="text-sm text-muted text-center self-center">
                {locale === "tr" ? "Önizleme için bir platform seçin" : "Pick a platform to preview"}
              </p>
            ) : (
              <PostPreview
                platform={previewPlatform}
                brandName={currentBrand?.trade_name ?? "Brand"}
                text={prompt}
                imageURL={previewImageURL}
              />
            )}
          </div>

          <p className="text-[11px] text-muted leading-relaxed">
            {locale === "tr"
              ? "Bu yalnızca bir önizlemedir. Yayınlanan içerik platformun stiline göre biraz farklı görünebilir."
              : "Approximation only. The published post may look slightly different depending on the platform."}
          </p>
        </aside>
      </form>
    </div>
  );
}

export const Route = createFileRoute("/app/content/new")({
  component: NewGeneration,
  validateSearch: (search: Record<string, unknown>) => ({
    brand_id: typeof search.brand_id === "string" ? search.brand_id : undefined,
  }),
});
